import React from "react";
import { MessageDescriptor } from "react-intl";

import {
	Checkbox,
	CheckboxGroup,
	CheckboxGroupColumn
} from "src/common/components/Checkbox";
import { useFormatMessageMap } from "src/common/hooks/useFormatMessageMap";
import { IsLockedContext } from "src/features/forms/context/IsLockedContext";
import {
	useLogProp,
	useMessageProps,
	useValue
} from "src/features/forms/hooks/useField";
import { CheckboxGroupProps } from "src/features/forms/types";

export const CheckboxGroupRenderer: React.FunctionComponent<
	CheckboxGroupProps
> = ({ direction, columns = 1, labels }) => {
	const isLocked = React.useContext(IsLockedContext);
	const options = useOptions(labels);
	const [value, onChangeValue] = useValue<string[] | undefined>(undefined);

	const groups: { label: string; value: string }[][] = [];
	for (let i = 0; i < columns; i++) {
		groups[i] = [];
	}
	for (let i = 0; i < options.length; i++) {
		groups[i % columns].push(options[i]);
	}

	return (
		<CheckboxGroup direction={direction} columns={columns}>
			{groups.map((options, i) => (
				<CheckboxGroupColumn key={i}>
					{options.map(option => (
						<Checkbox
							key={option.value}
							disabled={isLocked}
							checked={value?.includes(option.value)}
							onChangeChecked={checked => {
								const set = new Set(value || []);
								if (checked) {
									set.add(option.value);
								} else {
									set.delete(option.value);
								}
								if (set.size === 0) {
									onChangeValue(undefined);
								} else {
									onChangeValue(Array.from(set.values()));
								}
							}}
						>
							{option.label}
						</Checkbox>
					))}
				</CheckboxGroupColumn>
			))}
		</CheckboxGroup>
	);
};

function useOptions(messages: {
	[key: string]: MessageDescriptor;
}): { label: string; value: string }[] {
	const formatMessage = useFormatMessageMap();
	const messageProps = useMessageProps();
	const values = useLogProp<string[]>(messageProps, "values", []);
	return React.useMemo(
		() =>
			values.map(value => ({
				label: formatMessage(messages, value),
				value
			})),
		[messages, values, formatMessage]
	);
}
